/**
 * useAdvisorIntelligenceStats Hook
 * 
 * Fetches advisor intelligence metrics for the Advisor Passport.
 * Respects visibility rules (Tweak #1): managers see all, self-view requires unlock.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route self-view-unlock comparison through clock.
import { clock } from '@/utils/clock';
import { resolveMembershipRoles } from '@/lib/roles/multiRole';
import { isElevatedAuthority } from '@/lib/roles/offeredRoles';

export interface AdvisorIntelligenceStats {
  id: string;
  userId: string;
  shopId: string;
  periodStart: string;
  periodEnd: string;
  sampleCount: number;
  sampleStatus: 'learning' | 'established' | 'expert';
  // Decision Quality
  authLatencyAvgMinutes: number | null;
  authSuccessRate: number | null;
  approvedToCompletedRate: number | null;
  declinedReturnRate: number | null;
  // Diagnostic Translation
  diagTranslationScore: number | null;
  highConfStallRate: number | null;
  lowConfEscalationRate: number | null;
  // Workflow Impact
  middayWaiterRate: number | null;
  lateAuthRate: number | null;
  partsDelayRate: number | null;
  // Communication
  complaintConfirmedRate: number | null;
  clarificationLoopRate: number | null;
  // Composite
  advisorEffectivenessIndex: number | null;
  trendDirection: 'improving' | 'stable' | 'drifting' | null;
  // Visibility
  selfViewEnabled: boolean;
  selfViewUnlockedAt: string | null;
}

export interface AdvisorDecisionEvent {
  id: string;
  advisorUserId: string;
  shopId: string;
  ticketId: string | null;
  eventType: string;
  eventMetadata: Record<string, unknown>;
  detectedAt: string;
}

function transformStats(data: any): AdvisorIntelligenceStats {
  return {
    id: data.id,
    userId: data.user_id,
    shopId: data.shop_id,
    periodStart: data.period_start,
    periodEnd: data.period_end,
    sampleCount: data.sample_count || 0,
    sampleStatus: data.sample_status || 'learning',
    authLatencyAvgMinutes: data.auth_latency_avg_minutes,
    authSuccessRate: data.auth_success_rate,
    approvedToCompletedRate: data.approved_to_completed_rate,
    declinedReturnRate: data.declined_return_rate,
    diagTranslationScore: data.diag_translation_score,
    highConfStallRate: data.high_conf_stall_rate,
    lowConfEscalationRate: data.low_conf_escalation_rate,
    middayWaiterRate: data.midday_waiter_rate,
    lateAuthRate: data.late_auth_rate,
    partsDelayRate: data.parts_delay_rate,
    complaintConfirmedRate: data.complaint_confirmed_rate,
    clarificationLoopRate: data.clarification_loop_rate,
    advisorEffectivenessIndex: data.advisor_effectiveness_index,
    trendDirection: data.trend_direction,
    selfViewEnabled: data.self_view_enabled || false,
    selfViewUnlockedAt: data.self_view_unlocked_at,
  };
}

export function useAdvisorIntelligenceStats(userId: string | undefined) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['advisor-intelligence-stats', userId, activeShop?.id],
    queryFn: async () => {
      if (!userId || !activeShop?.id) return null;

      const { data, error } = await supabase
        .from('advisor_intelligence_stats')
        .select('*')
        .eq('user_id', userId)
        .eq('shop_id', activeShop.id)
        .order('period_end', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return transformStats(data);
    },
    enabled: !!userId && !!activeShop?.id,
  });
}

export function useAdvisorDecisionEvents(userId: string | undefined, limit = 20) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['advisor-decision-events', userId, activeShop?.id, limit],
    queryFn: async () => {
      if (!userId || !activeShop?.id) return [];

      const { data, error } = await supabase
        .from('advisor_decision_events')
        .select('*')
        .eq('advisor_user_id', userId)
        .eq('shop_id', activeShop.id)
        .order('detected_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((event) => ({
        id: event.id,
        advisorUserId: event.advisor_user_id,
        shopId: event.shop_id,
        ticketId: event.ticket_id,
        eventType: event.event_type,
        eventMetadata: event.event_metadata as Record<string, unknown>,
        detectedAt: event.detected_at,
      }));
    },
    enabled: !!userId && !!activeShop?.id,
  });
}

/**
 * Check if the current user can view the advisor's stats
 * (Tweak #1: Visibility rules)
 */
export function useCanViewAdvisorStats(advisorUserId: string | undefined) {
  const { user, activeShop } = useAuth();
  
  return useQuery({
    queryKey: ['can-view-advisor-stats', advisorUserId, user?.id, activeShop?.id],
    queryFn: async () => {
      if (!advisorUserId || !user?.id || !activeShop?.id) {
        return { canView: false, reason: 'missing_data' };
      }

      // MULTI-ROLE: this asked `memberships.role === 'MANAGER' | 'OWNER'`.
      //
      // That scalar used to carry authority. It does not any more: a member
      // created since the multi-role migration holds the NEUTRAL legacy value
      // `STAFF` with their real authority in `authority_role`, so an owner who
      // also turns wrenches — or any owner invited after the migration — read
      // as `STAFF` here and was refused access to a page they own. Authority
      // questions go through the canonical resolver, which is the one place
      // that knows how to read either shape.
      //
      // The SET is unchanged: OWNER and MANAGER, exactly as before.
      // `isElevatedAuthority` is that pair by definition; widening it to
      // ORG_ADMIN would be a product decision this repair was not given.
      const { data: membership } = await supabase
        .from('memberships')
        .select('role, authority_role')
        .eq('user_id', user.id)
        .eq('shop_id', activeShop.id)
        .eq('status', 'active')
        .single();

      if (isElevatedAuthority(resolveMembershipRoles(membership as never).authority)) {
        return { canView: true, reason: 'manager_or_owner', isSelfView: false };
      }

      // Check if viewing own stats
      if (user.id === advisorUserId) {
        // Check self-view unlock status
        const { data: stats } = await supabase
          .from('advisor_intelligence_stats')
          .select('self_view_enabled, self_view_unlocked_at')
          .eq('user_id', advisorUserId)
          .eq('shop_id', activeShop.id)
          .order('period_end', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!stats?.self_view_enabled) {
          return { canView: false, reason: 'self_view_not_enabled', isSelfView: true };
        }

        if (!stats.self_view_unlocked_at || new Date(stats.self_view_unlocked_at) > clock.now()) {
          return { canView: false, reason: 'self_view_not_unlocked_yet', isSelfView: true };
        }

        return { canView: true, reason: 'self_view', isSelfView: true };
      }

      return { canView: false, reason: 'no_access' };
    },
    enabled: !!advisorUserId && !!user?.id && !!activeShop?.id,
  });
}
